import type {
	NotificationFailureCode,
	NotificationStatus,
	NotificationType,
	SmsProviderCode,
} from './notification.types.js'

/**
 * How a tenant's SMS is sent (TO-383): through TODAY's Twilio account, or the
 * tenant's own. Derived from the binding — `BYO` exactly when credentials are
 * stored.
 */
export enum SmsCredentialMode {
	MANAGED = 'MANAGED',
	BYO = 'BYO',
}

/** The tenant's own Twilio API-key credentials. Write-only: never in a response. */
export interface SmsTwilioCredentialsInput {
	accountSid: string
	apiKeySid: string
	apiKeySecret: string
}

/** The send policy. `null` on a field means "no restriction". */
export interface SmsSendPolicy {
	/** ISO 3166-1 alpha-2 codes, uppercase. */
	allowedCountries: string[] | null
	/** Whole euros per UTC calendar month. */
	monthlySpendCapEur: number | null
	smsNotificationTypes: NotificationType[] | null
}

/** `PUT …/notifications/sms/settings` body: replaces the whole settings object. */
export interface SmsSettingsInput extends SmsSendPolicy {
	mode: SmsCredentialMode
	/** Twilio Messaging Service SID (`MG…`); required under `BYO`. */
	fromIdentity?: string | null
	/** Under `BYO`: absent keeps the stored credentials. Refused under `MANAGED`. */
	credentials?: SmsTwilioCredentialsInput
}

/** This month's estimated SMS spend. */
export interface SmsSpendSummaryDTO {
	/** `YYYY-MM`, UTC. */
	month: string
	estimatedSpendCents: number
	segmentPriceMicroEur: number
	capReached: boolean
}

/** The tenant's newest test SMS. */
export interface SmsLastTestDTO {
	notificationId: string
	status: NotificationStatus
	failureCode: NotificationFailureCode | null
	createdAt: string
}

/** `GET …/notifications/sms/settings` — never carries a credential or its reference. */
export interface SmsSettingsDTO extends SmsSendPolicy {
	configured: boolean
	mode: SmsCredentialMode
	provider: SmsProviderCode
	fromIdentity: string | null
	hasCredentials: boolean
	spend: SmsSpendSummaryDTO
	lastTest: SmsLastTestDTO | null
	updatedAt: string | null
}

/** `POST …/notifications/sms/test` body. */
export interface SmsTestInput {
	/** E.164. */
	to: string
}

/** A test SMS the provider accepted. */
export interface SmsTestResultDTO {
	notificationId: string
	status: NotificationStatus.SENT
}

/** The settings fields `notification.sms_settings_updated` may name, in the spec's order. */
export enum SmsSettingsField {
	MODE = 'mode',
	FROM_IDENTITY = 'fromIdentity',
	CREDENTIALS = 'credentials',
	ALLOWED_COUNTRIES = 'allowedCountries',
	MONTHLY_SPEND_CAP_EUR = 'monthlySpendCapEur',
	SMS_NOTIFICATION_TYPES = 'smsNotificationTypes',
}
